import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './AccuracyDashboardPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/* ── Types ────────────────────────────────────────────────────────────────── */

interface Metrics {
  tp: number;
  fn: number;
  tn: number;
  fp: number;
  precision: number;
  recall: number;
  f1: number;
  total: number;
}

interface TuningConfig {
  min_score_threshold?: number;
  min_name_similarity?: number;
  high_confidence_api_score?: number;
}

interface AccuracyVersion {
  id: number;
  label: string;
  description: string | null;
  tuning_config: TuningConfig | null;
  created_at: string;
  is_active: boolean;
}

interface EntityResult {
  id?: number;
  entity_name: string;
  entity_country?: string;
  test_type: 'positive' | 'negative';
  is_custom?: boolean;
  passed: boolean;
  expected: string;
  actual: string;
  details: Record<string, any>;
  tested_at?: string;
}

interface PoolStats {
  positive_count: number;
  negative_count: number;
  builtin_positive: number;
  builtin_negative: number;
  custom_positive: number;
  custom_negative: number;
  tested_positive?: number;
  tested_negative?: number;
  untested_positive?: number;
  untested_negative?: number;
}

interface VersionResults {
  version: AccuracyVersion;
  layer: string;
  metrics: Metrics;
  results: EntityResult[];
  false_negatives: EntityResult[];
  false_positives: EntityResult[];
  pool_stats: PoolStats;
}

interface CustomEntity {
  id: number;
  entity_name: string;
  country: string | null;
  test_type: string;
  expected_source: string | null;
  tricky_reason: string | null;
  created_at: string;
}

/* ── Constants ────────────────────────────────────────────────────────────── */

const SAMPLE_SIZE_OPTIONS = [10, 25, 50];

const DEFAULT_TUNING: TuningConfig = {
  min_score_threshold: 0.65,
  min_name_similarity: 0.25,
  high_confidence_api_score: 0.85,
};

/* ── Component ────────────────────────────────────────────────────────────── */

const AccuracyDashboardPage: React.FC = () => {
  // Version state
  const [versions, setVersions] = useState<AccuracyVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newTuning, setNewTuning] = useState<TuningConfig>({ ...DEFAULT_TUNING });
  const [creatingVersion, setCreatingVersion] = useState(false);

  // Test config
  const [layer, setLayer] = useState<'layer1' | 'layer2'>('layer1');
  const [sampleSize, setSampleSize] = useState(10);
  const [poolFilter, setPoolFilter] = useState<'all' | 'canonical' | 'alt_names' | 'truncated'>('all');

  // Pool & results state
  const [poolStats, setPoolStats] = useState<PoolStats | null>(null);
  const [versionResults, setVersionResults] = useState<VersionResults | null>(null);

  // Task state
  const [taskId, setTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number; step: string } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // UI state
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [copySuccess, setCopySuccess] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [rerunningIdx, setRerunningIdx] = useState<number | null>(null);

  // Custom entities state
  const [customEntities, setCustomEntities] = useState<CustomEntity[]>([]);
  const [showCustomPool, setShowCustomPool] = useState(false);
  const [showAddEntity, setShowAddEntity] = useState(false);
  const [newEntityName, setNewEntityName] = useState('');
  const [newEntityType, setNewEntityType] = useState<'positive' | 'negative'>('positive');
  const [newEntityCountry, setNewEntityCountry] = useState('');
  const [newEntitySource, setNewEntitySource] = useState('');
  const [newEntityReason, setNewEntityReason] = useState('');

  const selectedVersion = versions.find(v => v.id === selectedVersionId) || null;

  /* ── Data fetching ────────────────────────────────────────────────────── */

  const fetchVersions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/accuracy/versions`);
      setVersions(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchPoolStats = useCallback(async () => {
    try {
      const params: Record<string, any> = { pool_filter: poolFilter };
      if (selectedVersionId) params.version_id = selectedVersionId;
      if (selectedVersionId) params.layer = layer;
      const res = await axios.get(`${API_BASE}/accuracy/pool-stats`, { params });
      setPoolStats(res.data);
    } catch { /* ignore */ }
  }, [selectedVersionId, layer, poolFilter]);

  const fetchVersionResults = useCallback(async () => {
    if (!selectedVersionId) { setVersionResults(null); return; }
    try {
      const res = await axios.get(
        `${API_BASE}/accuracy/versions/${selectedVersionId}/results`,
        { params: { layer } }
      );
      setVersionResults(res.data);
    } catch { setVersionResults(null); }
  }, [selectedVersionId, layer]);

  const fetchCustomEntities = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/accuracy/custom-entities`);
      setCustomEntities(res.data);
    } catch { /* ignore */ }
  }, []);

  // Initial load
  useEffect(() => { fetchVersions(); fetchCustomEntities(); }, [fetchVersions, fetchCustomEntities]);

  // Refetch pool stats when version/layer changes
  useEffect(() => { fetchPoolStats(); }, [fetchPoolStats]);

  // Refetch version results when version/layer changes
  useEffect(() => { fetchVersionResults(); }, [fetchVersionResults]);

  /* ── Timer ────────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!running) { setElapsedSeconds(0); return; }
    const timer = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);

  /* ── Task polling ─────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!taskId || !running) return;
    let consecutiveErrors = 0;

    const poll = setInterval(async () => {
      try {
        const res = await axios.get(`${API_BASE}/accuracy/status/${taskId}`);
        const data = res.data;
        consecutiveErrors = 0;

        if (data.state === 'SUCCESS') {
          clearInterval(poll);
          setRunning(false);
          setProgress(null);
          // Refresh cumulative results from server
          fetchVersionResults();
          fetchPoolStats();
        } else if (data.state === 'FAILURE') {
          clearInterval(poll);
          setError(data.error || 'Accuracy test failed.');
          setRunning(false);
          setProgress(null);
        } else if (data.state === 'PROGRESS' && data.status) {
          setProgress({
            current: data.status.current || 0,
            total: data.status.total || sampleSize * 2,
            step: data.status.step || 'Processing...',
          });
        }
      } catch {
        consecutiveErrors++;
        if (consecutiveErrors > 30) {
          clearInterval(poll);
          setError('Lost connection to server. The task may still be running.');
          setRunning(false);
        }
      }
    }, 2000);

    return () => clearInterval(poll);
  }, [taskId, running, sampleSize, fetchVersionResults, fetchPoolStats]);

  /* ── Handlers ─────────────────────────────────────────────────────────── */

  const handleCreateVersion = async () => {
    if (!newLabel.trim()) return;
    setCreatingVersion(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/accuracy/versions`, {
        label: newLabel.trim(),
        description: newDescription.trim() || null,
        tuning_config: newTuning,
      });
      await fetchVersions();
      setSelectedVersionId(res.data.id);
      setShowNewVersion(false);
      setNewLabel('');
      setNewDescription('');
      setNewTuning({ ...DEFAULT_TUNING });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create version');
    } finally {
      setCreatingVersion(false);
    }
  };

  const handleRun = useCallback(async () => {
    setError(null);
    setProgress(null);
    setExpandedRows(new Set());
    setRunning(true);

    try {
      const payload: Record<string, any> = {
        layer,
        n_positive: sampleSize,
        n_negative: sampleSize,
        pool_filter: poolFilter,
      };
      if (selectedVersionId) {
        payload.version_id = selectedVersionId;
      }
      const res = await axios.post(`${API_BASE}/accuracy/run`, payload);
      setTaskId(res.data.task_id);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start accuracy test.');
      setRunning(false);
    }
  }, [layer, sampleSize, selectedVersionId, poolFilter]);

  const handleAddCustomEntity = async () => {
    if (!newEntityName.trim()) return;
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/accuracy/custom-entities`, {
        entity_name: newEntityName.trim(),
        country: newEntityCountry.trim() || null,
        test_type: newEntityType,
        expected_source: newEntityType === 'positive' ? newEntitySource.trim() || null : null,
        tricky_reason: newEntityType === 'negative' ? newEntityReason.trim() || null : null,
      });
      // Show bulk upload summary if applicable
      if (res.data.created !== undefined) {
        const msg = `Added ${res.data.created} entit${res.data.created === 1 ? 'y' : 'ies'}` +
          (res.data.skipped ? `, ${res.data.skipped} skipped (duplicate)` : '');
        setError(null);
        alert(msg);
      }
      setNewEntityName('');
      setNewEntityCountry('');
      setNewEntitySource('');
      setNewEntityReason('');
      setShowAddEntity(false);
      fetchCustomEntities();
      fetchPoolStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add entity');
    }
  };

  const handleDeleteCustomEntity = async (id: number) => {
    try {
      await axios.delete(`${API_BASE}/accuracy/custom-entities/${id}`);
      fetchCustomEntities();
      fetchPoolStats();
    } catch { /* ignore */ }
  };

  const handleResetVersion = async () => {
    if (!selectedVersionId) return;
    if (!window.confirm(`Reset all ${layer} results for this version? This cannot be undone.`)) return;
    setResetting(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/accuracy/versions/${selectedVersionId}/reset`, { layer });
      fetchVersionResults();
      fetchPoolStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset version');
    } finally {
      setResetting(false);
    }
  };

  const handleRerunEntity = async (idx: number, result: EntityResult) => {
    if (!selectedVersionId) return;
    setRerunningIdx(idx);
    setError(null);
    try {
      await axios.post(`${API_BASE}/accuracy/rerun-entity`, {
        version_id: selectedVersionId,
        entity_name: result.entity_name,
        test_type: result.test_type,
        layer,
      });
      fetchVersionResults();
      fetchPoolStats();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to rerun entity');
    } finally {
      setRerunningIdx(null);
    }
  };

  const toggleRow = useCallback((idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

  // Derive display data from version results
  const metrics = versionResults?.metrics || null;
  const results = versionResults?.results || [];
  const falseNegatives = versionResults?.false_negatives || [];
  const falsePositives = versionResults?.false_positives || [];
  const resultPoolStats = versionResults?.pool_stats || poolStats;

  const sortedResults = [...results].sort((a, b) => {
    if (a.passed === b.passed) return 0;
    return a.passed ? 1 : -1;
  });

  const passCount = sortedResults.filter(r => r.passed).length;
  const failCount = sortedResults.filter(r => !r.passed).length;

  const hasUntested = resultPoolStats &&
    ((resultPoolStats.untested_positive ?? 0) > 0 || (resultPoolStats.untested_negative ?? 0) > 0);
  const allTested = resultPoolStats &&
    (resultPoolStats.untested_positive ?? 0) === 0 && (resultPoolStats.untested_negative ?? 0) === 0
    && results.length > 0;

  const handleCopyFPFN = () => {
    const lines: string[] = [];

    if (falseNegatives.length > 0) {
      lines.push('=== False Negatives (Missed CSL Entities) ===');
      for (const r of falseNegatives) {
        const d = r.details || {};
        const totalRaw = d.total_raw ?? '?';
        const kept = d.csl_total ?? '?';
        const src = d.entity?.expected_source || '?';
        lines.push(`Entity: ${r.entity_name} | Expected: ${src} | Raw API matches: ${totalRaw} | Kept: ${kept}`);
        if (d.filtered_out_matches) {
          for (const m of d.filtered_out_matches) {
            const parts = [`"${m.name}" score=${m.effective_score ?? m.score ?? '?'}`];
            if (m.name_similarity !== undefined && m.name_similarity !== null) parts.push(`name_sim=${m.name_similarity}`);
            parts.push(`(${m.filter_reason})`);
            lines.push(`  Filtered: ${parts.join(' ')}`);
          }
        }
        lines.push('');
      }
    }

    if (falsePositives.length > 0) {
      lines.push('=== False Positives (Clean Entities Flagged) ===');
      for (const r of falsePositives) {
        const d = r.details || {};
        const matched = d.matched_entities?.[0];
        if (matched) {
          const parts = [`"${matched.name}" score=${matched.score?.toFixed(2) ?? '?'}`];
          if (matched.name_similarity !== undefined) parts.push(`name_sim=${matched.name_similarity}`);
          lines.push(`Entity: ${r.entity_name} | Matched: ${parts.join(' ')}`);
        } else {
          lines.push(`Entity: ${r.entity_name} | ${d.failure_reason || 'flagged'}`);
        }
        lines.push('');
      }
    }

    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  /* ── Render ──────────────────────────────────────────────────────────── */

  return (
    <div className="accuracy-page">
      <h2>CSL Screening Accuracy Dashboard</h2>
      <p className="page-description">
        Test screening accuracy by running known CSL entities and clean entities through the pipeline.
        Create versions with different tuning configs to compare results.
      </p>

      {/* ── Version Selector ──────────────────────────────────────────── */}
      <div className="version-selector">
        <div className="version-selector-row">
          <div className="version-dropdown-wrap">
            <label>Version</label>
            <select
              value={selectedVersionId ?? ''}
              onChange={e => {
                const val = e.target.value;
                setSelectedVersionId(val ? parseInt(val) : null);
              }}
              disabled={running}
            >
              <option value="">— Select Version —</option>
              {versions.map(v => (
                <option key={v.id} value={v.id}>{v.label}</option>
              ))}
            </select>
          </div>
          <button
            className="new-version-btn"
            onClick={() => setShowNewVersion(!showNewVersion)}
            disabled={running}
          >
            + New Version
          </button>
        </div>

        {/* Selected version info */}
        {selectedVersion && !showNewVersion && (
          <div className="version-info">
            <div className="version-info-header">
              <strong>{selectedVersion.label}</strong>
              {selectedVersion.description && (
                <span className="version-desc"> — {selectedVersion.description}</span>
              )}
            </div>
            {selectedVersion.tuning_config && (
              <div className="tuning-display">
                <span>Score: {selectedVersion.tuning_config.min_score_threshold ?? DEFAULT_TUNING.min_score_threshold}</span>
                <span>Name Sim: {selectedVersion.tuning_config.min_name_similarity ?? DEFAULT_TUNING.min_name_similarity}</span>
                <span>High Conf: {selectedVersion.tuning_config.high_confidence_api_score ?? DEFAULT_TUNING.high_confidence_api_score}</span>
              </div>
            )}
          </div>
        )}

        {/* No versions prompt */}
        {versions.length === 0 && !showNewVersion && (
          <div className="no-versions-prompt">
            No versions yet. Create your first version to start tracking accuracy results.
          </div>
        )}

        {/* New version form */}
        {showNewVersion && (
          <div className="new-version-form">
            <div className="form-row">
              <label>Label *</label>
              <input
                type="text"
                value={newLabel}
                onChange={e => setNewLabel(e.target.value)}
                placeholder="e.g., v1 - baseline"
                maxLength={200}
              />
            </div>
            <div className="form-row">
              <label>Description</label>
              <input
                type="text"
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>

            <div className="tuning-panel">
              <h4>Tuning Configuration</h4>
              <div className="slider-group">
                <label>
                  Min Score Threshold
                  <span className="slider-value">{newTuning.min_score_threshold}</span>
                </label>
                <input
                  type="range"
                  min={0.50} max={0.90} step={0.05}
                  value={newTuning.min_score_threshold}
                  onChange={e => setNewTuning(t => ({ ...t, min_score_threshold: parseFloat(e.target.value) }))}
                />
                <p className="slider-help">Minimum score to keep a match. Higher = fewer FP, lower = fewer FN.</p>
              </div>
              <div className="slider-group">
                <label>
                  Min Name Similarity
                  <span className="slider-value">{newTuning.min_name_similarity}</span>
                </label>
                <input
                  type="range"
                  min={0.10} max={0.60} step={0.05}
                  value={newTuning.min_name_similarity}
                  onChange={e => setNewTuning(t => ({ ...t, min_name_similarity: parseFloat(e.target.value) }))}
                />
                <p className="slider-help">Minimum name similarity. Higher = stricter matching.</p>
              </div>
              <div className="slider-group">
                <label>
                  High Confidence Bypass
                  <span className="slider-value">{newTuning.high_confidence_api_score}</span>
                </label>
                <input
                  type="range"
                  min={0.70} max={0.95} step={0.05}
                  value={newTuning.high_confidence_api_score}
                  onChange={e => setNewTuning(t => ({ ...t, high_confidence_api_score: parseFloat(e.target.value) }))}
                />
                <p className="slider-help">Scores above this bypass filters entirely.</p>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="create-version-btn"
                onClick={handleCreateVersion}
                disabled={creatingVersion || !newLabel.trim()}
              >
                {creatingVersion ? 'Creating...' : 'Create Version'}
              </button>
              <button className="cancel-btn" onClick={() => setShowNewVersion(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Configuration ─────────────────────────────────────────────── */}
      <div className="accuracy-config">
        <div className="config-section">
          <label>Screening Layer</label>
          <div className="layer-options">
            <label className={`layer-option ${layer === 'layer1' ? 'selected' : ''}`}>
              <input type="radio" name="layer" value="layer1"
                checked={layer === 'layer1'} onChange={() => setLayer('layer1')} disabled={running} />
              <div><strong>Layer 1</strong> — CSL Search Only</div>
            </label>
            <label className={`layer-option ${layer === 'layer2' ? 'selected' : ''}`}>
              <input type="radio" name="layer" value="layer2"
                checked={layer === 'layer2'} onChange={() => setLayer('layer2')} disabled={running} />
              <div><strong>Layer 2</strong> — CSL + AI Validation</div>
            </label>
          </div>
        </div>

        <div className="config-section">
          <label>Sample Size (per type)</label>
          <div className="layer-options">
            {SAMPLE_SIZE_OPTIONS.map(n => (
              <label key={n} className={`layer-option ${sampleSize === n ? 'selected' : ''}`}>
                <input type="radio" name="sampleSize" value={n}
                  checked={sampleSize === n} onChange={() => setSampleSize(n)} disabled={running} />
                <div>{n} + {n} = {n * 2} entities</div>
              </label>
            ))}
          </div>
        </div>

        <div className="config-section">
          <label>Positive Pool</label>
          <div className="layer-options">
            <label className={`layer-option ${poolFilter === 'all' ? 'selected' : ''}`}>
              <input type="radio" name="poolFilter" value="all"
                checked={poolFilter === 'all'} onChange={() => setPoolFilter('all')} disabled={running} />
              <div><strong>All</strong> — Canonical + Alt Names</div>
            </label>
            <label className={`layer-option ${poolFilter === 'canonical' ? 'selected' : ''}`}>
              <input type="radio" name="poolFilter" value="canonical"
                checked={poolFilter === 'canonical'} onChange={() => setPoolFilter('canonical')} disabled={running} />
              <div><strong>Canonical</strong> — Primary names only</div>
            </label>
            <label className={`layer-option ${poolFilter === 'alt_names' ? 'selected' : ''}`}>
              <input type="radio" name="poolFilter" value="alt_names"
                checked={poolFilter === 'alt_names'} onChange={() => setPoolFilter('alt_names')} disabled={running} />
              <div><strong>Alt Names</strong> — Trade/alternate names only</div>
            </label>
            <label className={`layer-option ${poolFilter === 'truncated' ? 'selected' : ''}`}>
              <input type="radio" name="poolFilter" value="truncated"
                checked={poolFilter === 'truncated'} onChange={() => setPoolFilter('truncated')} disabled={running} />
              <div><strong>Truncated</strong> — Shortened/partial names</div>
            </label>
          </div>
        </div>

        {resultPoolStats && (
          <p className="pool-info">
            Pool: {resultPoolStats.positive_count} positive, {resultPoolStats.negative_count} negative
            {(resultPoolStats.custom_positive > 0 || resultPoolStats.custom_negative > 0)
              ? ` (incl. ${resultPoolStats.custom_positive + resultPoolStats.custom_negative} custom)`
              : ''}
            {resultPoolStats.untested_positive !== undefined && (
              <> | Untested: {resultPoolStats.untested_positive} pos, {resultPoolStats.untested_negative} neg</>
            )}
          </p>
        )}

        {/* Summary bar when results exist */}
        {metrics && metrics.total > 0 && (
          <div className="tested-summary-bar">
            {metrics.total} tested ({(metrics.tp + metrics.fn)} pos, {(metrics.tn + metrics.fp)} neg)
            {resultPoolStats?.untested_positive !== undefined && (
              <> | {resultPoolStats.untested_positive} pos untested, {resultPoolStats.untested_negative} neg untested</>
            )}
          </div>
        )}

        <div className="run-button-row">
          <button
            className="run-button"
            onClick={handleRun}
            disabled={running || (!selectedVersionId && versions.length > 0) || (allTested ?? false)}
          >
            {running
              ? 'Running...'
              : allTested
                ? 'All Entities Tested'
                : metrics && metrics.total > 0
                  ? `Run More${hasUntested ? ` (${(resultPoolStats?.untested_positive ?? 0) + (resultPoolStats?.untested_negative ?? 0)} untested)` : ''}`
                  : 'Run Accuracy Test'
            }
          </button>
          {metrics && metrics.total > 0 && selectedVersionId && (
            <button
              className="reset-version-btn"
              onClick={handleResetVersion}
              disabled={running || resetting}
            >
              {resetting ? 'Resetting...' : 'Reset Results'}
            </button>
          )}
        </div>
        {!selectedVersionId && versions.length > 0 && (
          <p className="hint-text">Select or create a version to run tests.</p>
        )}
      </div>

      {/* ── Error ─────────────────────────────────────────────────────── */}
      {error && <div className="accuracy-error">{error}</div>}

      {/* ── Progress ──────────────────────────────────────────────────── */}
      {running && (
        <div className="accuracy-progress">
          {progress && (
            <div className="progress-bar-container">
              <div className="progress-bar-fill"
                style={{ width: `${Math.max((progress.current / progress.total) * 100, 2)}%` }}>
                {progress.current}/{progress.total}
              </div>
            </div>
          )}
          <p className="progress-status">
            {progress?.step || 'Starting...'}
            <span className="elapsed-time">
              {' '}({Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')} elapsed)
            </span>
          </p>
        </div>
      )}

      {/* ── Results ───────────────────────────────────────────────────── */}
      {metrics && metrics.total > 0 && (
        <>
          {/* Metric Cards */}
          <div className="metrics-cards">
            <div className="metric-card">
              <div className="metric-label">
                Precision
                <span className="help-icon" title="Of entities flagged as matches, what % were actual CSL entities?">?</span>
              </div>
              <div className="metric-value">{pct(metrics.precision)}</div>
              <div className="metric-tooltip">Of entities flagged, what % were actual CSL entities?</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">
                Recall
                <span className="help-icon" title="Of actual CSL entities, what % were correctly flagged?">?</span>
              </div>
              <div className="metric-value">{pct(metrics.recall)}</div>
              <div className="metric-tooltip">Of actual CSL entities, what % were flagged?</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">
                F1 Score
                <span className="help-icon" title="Harmonic mean of Precision and Recall">?</span>
              </div>
              <div className="metric-value">{pct(metrics.f1)}</div>
              <div className="metric-tooltip">Harmonic mean of Precision and Recall</div>
            </div>
          </div>

          {/* Confusion Matrix */}
          <div className="confusion-matrix-section">
            <h3>Confusion Matrix</h3>
            <div className="confusion-matrix">
              <div className="cm-header"></div>
              <div className="cm-header">Predicted Positive</div>
              <div className="cm-header">Predicted Negative</div>
              <div className="cm-row-header">Actual Positive</div>
              <div className="cm-cell tp">
                <div className="cm-value">{metrics.tp}</div>
                <div className="cm-label">TP</div>
              </div>
              <div className="cm-cell fn">
                <div className="cm-value">{metrics.fn}</div>
                <div className="cm-label">FN</div>
              </div>
              <div className="cm-row-header">Actual Negative</div>
              <div className="cm-cell fp">
                <div className="cm-value">{metrics.fp}</div>
                <div className="cm-label">FP</div>
              </div>
              <div className="cm-cell tn">
                <div className="cm-value">{metrics.tn}</div>
                <div className="cm-label">TN</div>
              </div>
            </div>
          </div>

          {/* ── FP/FN Spotlight ────────────────────────────────────────── */}
          {(falseNegatives.length > 0 || falsePositives.length > 0) && (
            <div className="fpfn-spotlight">
              <div className="fpfn-header">
                <button className="copy-fpfn-btn" onClick={handleCopyFPFN}>
                  {copySuccess ? 'Copied!' : 'Copy FP/FN Report'}
                </button>
              </div>
              {falseNegatives.length > 0 && (
                <div className="spotlight-section fn-section">
                  <h4>False Negatives — Missed CSL Entities ({falseNegatives.length})</h4>
                  <table className="spotlight-table">
                    <thead>
                      <tr>
                        <th>Entity</th>
                        <th>Expected Source</th>
                        <th>CSL Matches</th>
                        <th>Top Filtered Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {falseNegatives.map((r, i) => {
                        const d = r.details || {};
                        const kept = d.csl_total ?? 0;
                        const raw = d.total_raw ?? 0;
                        const filtered = d.filtered_out_matches as Array<{
                          name: string; score?: number; effective_score?: number;
                          name_similarity?: number; filter_reason: string;
                        }> | undefined;
                        const topMatch = filtered?.[0];
                        const tuning = d.tuning_config;
                        return (
                          <tr key={i}>
                            <td>{r.entity_name}</td>
                            <td><code>{d.entity?.expected_source || '?'}</code></td>
                            <td>{kept} kept / {raw} raw</td>
                            <td>
                              {topMatch ? (
                                <>
                                  <span className="filtered-match-detail">
                                    <code>{topMatch.name}</code>{' '}
                                    score={topMatch.effective_score ?? topMatch.score ?? '?'}
                                    {topMatch.name_similarity !== undefined && topMatch.name_similarity !== null
                                      && ` name_sim=${topMatch.name_similarity}`}
                                  </span>
                                  <div className="filter-reason">filtered: {topMatch.filter_reason}</div>
                                  {topMatch.filter_reason === 'low_score' && tuning?.min_score_threshold && (
                                    <div className="actionable-hint">
                                      score {topMatch.effective_score ?? topMatch.score} &lt; threshold {tuning.min_score_threshold} — lower min_score_threshold to {Math.max(0.50, ((topMatch.effective_score ?? topMatch.score ?? 0) - 0.02)).toFixed(2)} to catch this
                                    </div>
                                  )}
                                  {topMatch.filter_reason === 'low_name_similarity' && tuning?.min_name_similarity && (
                                    <div className="actionable-hint">
                                      name_sim {topMatch.name_similarity} &lt; threshold {tuning.min_name_similarity} — lower min_name_similarity to {Math.max(0.10, ((topMatch.name_similarity ?? 0) - 0.02)).toFixed(2)} to catch this
                                    </div>
                                  )}
                                  {filtered && filtered.length > 1 && (
                                    <div className="filter-reason" style={{ marginTop: 2 }}>
                                      +{filtered.length - 1} more filtered
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span className="filter-reason">
                                  {raw === 0 ? 'No API results' : (d.failure_reason || 'Unknown')}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {falsePositives.length > 0 && (
                <div className="spotlight-section fp-section">
                  <h4>False Positives — Clean Entities Flagged ({falsePositives.length})</h4>
                  <table className="spotlight-table">
                    <thead>
                      <tr>
                        <th>Entity</th>
                        <th>Matched CSL Name</th>
                        <th>Score</th>
                        <th>Relationship</th>
                      </tr>
                    </thead>
                    <tbody>
                      {falsePositives.map((r, i) => {
                        const d = r.details || {};
                        const matched = d.matched_entities?.[0];
                        return (
                          <tr key={i}>
                            <td>{r.entity_name}</td>
                            <td>{matched?.name || '?'}</td>
                            <td>{matched?.score !== undefined ? matched.score.toFixed(2) : '?'}</td>
                            <td>
                              {matched?.relationship || d.relationships?.join(', ') || '?'}
                              {matched?.score && matched.score < 0.85 && (
                                <div className="actionable-hint">Try raising min_score_threshold</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Detailed Results Table ─────────────────────────────────── */}
          <div className="results-table-section">
            <h3>Detailed Results</h3>
            <div className="results-summary-bar">
              <span className="summary-item pass">{passCount} Passed</span>
              <span className="summary-item fail">{failCount} Failed</span>
              <span style={{ color: '#718096', fontSize: '13px' }}>
                Layer: {layer}{selectedVersion ? ` | Version: ${selectedVersion.label}` : ''}
              </span>
            </div>

            <table className="results-table">
              <thead>
                <tr>
                  <th>Entity Name</th>
                  <th>Type</th>
                  <th>Result</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.map((r, idx) => (
                  <React.Fragment key={idx}>
                    <tr className={r.passed ? '' : 'fail-row'}>
                      <td>
                        {r.entity_name}
                        {r.is_custom && <span className="custom-badge">custom</span>}
                      </td>
                      <td>
                        <span className={`type-badge ${r.test_type}`}>
                          {r.test_type === 'positive' ? 'Positive' : 'Negative'}
                        </span>
                      </td>
                      <td>
                        <span className={`result-badge ${r.passed ? 'pass' : 'fail'}`}>
                          {r.passed ? 'Pass' : 'Fail'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '13px' }}>{r.actual}</span>
                        {' '}
                        <button className="expand-toggle" onClick={() => toggleRow(idx)}>
                          {expandedRows.has(idx) ? 'hide' : 'details'}
                        </button>
                        {' '}
                        <button
                          className="rerun-btn"
                          onClick={() => handleRerunEntity(idx, r)}
                          disabled={rerunningIdx !== null || running}
                          title="Rerun this entity"
                        >
                          {rerunningIdx === idx ? 'Rerunning...' : 'Rerun'}
                        </button>
                      </td>
                    </tr>
                    {expandedRows.has(idx) && (
                      <tr className="detail-row">
                        <td colSpan={4}>
                          <div className="detail-content">
                            {r.details.failure_reason && (
                              <div><strong>Failure reason:</strong> {r.details.failure_reason}</div>
                            )}
                            {r.details.csl_total !== undefined && (
                              <div>CSL matches: <code>{r.details.csl_total}</code> kept / <code>{r.details.total_raw ?? '?'}</code> raw
                                {r.details.weak_matches_filtered > 0 && ` (${r.details.weak_matches_filtered} filtered)`}
                              </div>
                            )}
                            {r.details.filtered_out_matches && (
                              <div>
                                Filtered out: {r.details.filtered_out_matches.map((m: any, i: number) => (
                                  <div key={i} className="filtered-match-detail" style={{ marginLeft: 12 }}>
                                    <code>{m.name}</code> score={m.effective_score ?? m.score ?? '?'}
                                    {m.name_similarity != null && ` name_sim=${m.name_similarity}`}
                                    {' '}— <em>{m.filter_reason}</em>
                                  </div>
                                ))}
                              </div>
                            )}
                            {r.details.relationships && (
                              <div>AI relationships: <code>{r.details.relationships.join(', ')}</code></div>
                            )}
                            {r.details.matched_entities && (
                              <div>
                                Matched: {r.details.matched_entities.map((m: any, i: number) => (
                                  <span key={i}>
                                    {i > 0 && ', '}
                                    <code>{m.name}</code>
                                    {m.score !== undefined && ` (score: ${m.score})`}
                                    {m.relationship && ` [${m.relationship}]`}
                                  </span>
                                ))}
                              </div>
                            )}
                            {r.details.error && (
                              <div><strong>Error:</strong> <code>{r.details.error}</code></div>
                            )}
                            {r.details.entity?.expected_source && (
                              <div>Expected source: <code>{r.details.entity.expected_source}</code></div>
                            )}
                            {r.details.entity?.tricky_reason && (
                              <div>Tricky reason: {r.details.entity.tricky_reason}</div>
                            )}
                            {r.details.retry_without_country && (
                              <div><strong>Country fallback:</strong> Retried without country filter (original country query returned 0 results)</div>
                            )}
                            {r.details.tuning_config && (
                              <div>Tuning: score={r.details.tuning_config.min_score_threshold}, nameSim={r.details.tuning_config.min_name_similarity}, highConf={r.details.tuning_config.high_confidence_api_score}</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Custom Entity Pool ────────────────────────────────────────── */}
      <div className="custom-pool-section">
        <button className="collapsible-header" onClick={() => setShowCustomPool(!showCustomPool)}>
          Custom Pool ({customEntities.length} entities) {showCustomPool ? '▾' : '▸'}
        </button>

        {showCustomPool && (
          <div className="custom-pool-body">
            <button className="add-entity-btn" onClick={() => setShowAddEntity(!showAddEntity)}>
              + Add Entity
            </button>

            {showAddEntity && (
              <div className="add-entity-form">
                <div className="form-row">
                  <label>Entity Name(s) *</label>
                  <textarea value={newEntityName}
                    onChange={e => setNewEntityName(e.target.value)}
                    placeholder="Entity name (or multiple names separated by semicolons)"
                    rows={newEntityName.includes(';') ? 4 : 1}
                    style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 'inherit' }} />
                  <span className="field-hint">Separate multiple names with semicolons for bulk upload</span>
                </div>
                <div className="form-row">
                  <label>Type</label>
                  <div className="layer-options">
                    <label className={`layer-option ${newEntityType === 'positive' ? 'selected' : ''}`}>
                      <input type="radio" name="entityType" value="positive"
                        checked={newEntityType === 'positive'}
                        onChange={() => setNewEntityType('positive')} />
                      <div>Positive (CSL entity)</div>
                    </label>
                    <label className={`layer-option ${newEntityType === 'negative' ? 'selected' : ''}`}>
                      <input type="radio" name="entityType" value="negative"
                        checked={newEntityType === 'negative'}
                        onChange={() => setNewEntityType('negative')} />
                      <div>Negative (clean)</div>
                    </label>
                  </div>
                </div>
                <div className="form-row">
                  <label>Country (ISO)</label>
                  <input type="text" value={newEntityCountry}
                    onChange={e => setNewEntityCountry(e.target.value)}
                    placeholder="e.g., US" maxLength={5} style={{ width: '80px' }} />
                </div>
                {newEntityType === 'positive' && (
                  <div className="form-row">
                    <label>Expected CSL Source</label>
                    <input type="text" value={newEntitySource}
                      onChange={e => setNewEntitySource(e.target.value)}
                      placeholder="e.g., SDN, EL" />
                  </div>
                )}
                {newEntityType === 'negative' && (
                  <div className="form-row">
                    <label>Tricky Reason</label>
                    <input type="text" value={newEntityReason}
                      onChange={e => setNewEntityReason(e.target.value)}
                      placeholder="Why is this a tricky test case?" />
                  </div>
                )}
                <div className="form-actions">
                  <button className="create-version-btn" onClick={handleAddCustomEntity}
                    disabled={!newEntityName.trim()}>
                    {newEntityName.includes(';') ? `Add ${newEntityName.split(';').filter(n => n.trim()).length} Entities` : 'Add'}
                  </button>
                  <button className="cancel-btn" onClick={() => setShowAddEntity(false)}>Cancel</button>
                </div>
              </div>
            )}

            {customEntities.length === 0 ? (
              <p className="no-custom-entities">No custom entities yet. Add entities to expand the test pool.</p>
            ) : (
              <table className="results-table custom-entity-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Country</th>
                    <th>Source / Reason</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {customEntities.map(e => (
                    <tr key={e.id}>
                      <td>{e.entity_name}</td>
                      <td>
                        <span className={`type-badge ${e.test_type}`}>
                          {e.test_type === 'positive' ? 'Positive' : 'Negative'}
                        </span>
                      </td>
                      <td>{e.country || '—'}</td>
                      <td>{e.expected_source || e.tricky_reason || '—'}</td>
                      <td>
                        <button className="delete-entity-btn" onClick={() => handleDeleteCustomEntity(e.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccuracyDashboardPage;
